import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Loader,
  toast,
} from "@devhop/ui";
import { ArrowRightLeft } from "lucide-react";
import { useEffect, useState } from "react";
import type { BulkCreateExchangeRatesDTO } from "@/modules/merchant/domain/contracts";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  buildInitialFxRateInputsFromItems,
  exchangeRateKey,
  SHOP_FX_EDIT_PAIRS,
} from "@/shared/lib/exchange-rate";
import { canUseMerchantPermission } from "../api/client";
import {
  useBulkCreateExchangeRatesMutation,
  useExchangeRatesQuery,
} from "../api/queries";
import { ShopExchangeRateHistorySection } from "./ShopExchangeRateHistorySection";

/** ອັດຕາແລກປ່ຽນ (MC3) — ໃຊ້ທັງແດຊບອດ ແລະ ໜ້າ settings */
export function ShopExchangeRatesSection({
  shopId,
  sectionId,
}: {
  shopId: string | undefined;
  sectionId?: string;
}) {
  const { currentShop } = useMerchantShops(shopId);
  const exchangeRatesQuery = useExchangeRatesQuery(shopId);
  const bulkExchangeRates = useBulkCreateExchangeRatesMutation(shopId);

  const [fxRates, setFxRates] = useState<Record<string, string>>({});
  const [fxSource, setFxSource] = useState<string>("");

  const canEditFx =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.shop_staff.update,
      currentShop?.verifiedLevel ?? null,
    );

  useEffect(() => {
    const items = exchangeRatesQuery.data?.items;
    if (!items) return;
    setFxRates(buildInitialFxRateInputsFromItems(items));
  }, [exchangeRatesQuery.data?.items]);

  return (
    <RequireMerchantPermission permission={MerchantPermissions.pos.read}>
      <div id={sectionId} className="space-y-6 scroll-mt-24">
      <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
        <CardContent className="space-y-5 sm:space-y-6">
          <div className="flex flex-col gap-3 border-b border-border/60 pb-4 sm:flex-row sm:items-start sm:gap-4">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
              <ArrowRightLeft className="size-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
            <h2 className="font-heading font-semibold text-base text-foreground sm:text-lg">
              ອັດຕາແລກປ່ຽນ (MC3)
            </h2>
            <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
              1 ຫົວຫນ່ວຍສະກຸນຊ້າຍ = ຈຳນວນ rate ສະກຸນຂວາ. ສຳລັບລາຍງານ/ຄຳນວນ — ບໍ່ແກ້
              POS ledger. ຄ່າທົ່ວແພລດ (ບໍ່ມີ shop) ຈະໃຊ້ເມື່ອບໍ່ມີອັດຕາຂອງຮ້ານສຳລັບຄູ່ນັ້ນ.
            </p>
            {exchangeRatesQuery.data?.asOf ? (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 py-1 font-mono text-muted-foreground text-xs">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  ອ້າງອີງ
                </span>
                {formatDateTimeLocal(exchangeRatesQuery.data.asOf)}
              </p>
            ) : null}
            </div>
          </div>

          {exchangeRatesQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <Loader />
            </div>
          ) : exchangeRatesQuery.error ? (
            <p className="text-destructive text-sm">ໂຫຼດອັດຕາບໍ່ສຳເລັດ</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/80 bg-card/50 ring-1 ring-border/30">
              <ul className="divide-y divide-border/80">
                {SHOP_FX_EDIT_PAIRS.map(({ from, to }) => {
                  const k = exchangeRateKey(from, to);
                  return (
                    <li
                      key={k}
                      className="flex flex-col gap-2 bg-card/30 px-3 py-3 transition-colors hover:bg-muted/30 sm:flex-row sm:items-center sm:gap-4"
                    >
                      <div className="flex min-w-0 shrink-0 items-center gap-2 sm:w-48">
                        <span className="font-mono text-foreground text-sm font-medium tracking-tight">
                          {from}
                        </span>
                        <span className="text-muted-foreground text-sm" aria-hidden>
                          →
                        </span>
                        <span className="font-mono text-foreground text-sm font-medium tracking-tight">
                          {to}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <Label htmlFor={`fx-${k}`} className="sr-only">
                          Rate {from} to {to}
                        </Label>
                        <Input
                          id={`fx-${k}`}
                          type="text"
                          inputMode="decimal"
                          placeholder="rate"
                          value={fxRates[k] ?? ""}
                          onChange={(e) =>
                            setFxRates((prev) => ({
                              ...prev,
                              [k]: e.target.value,
                            }))
                          }
                          disabled={!shopId || !canEditFx}
                          className="font-mono"
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <RequireMerchantPermission
            permission={MerchantPermissions.shop_staff.update}
          >
            <div className="space-y-4 rounded-xl border border-dashed border-border/80 bg-muted/10 p-4 sm:p-5">
              <div className="space-y-2">
                <Label htmlFor="fx-source">ແຫຼ່ງ (ໃຊ້ກັບຊຸດນີ້ທັງໝົດ)</Label>
                <Input
                  id="fx-source"
                  type="text"
                  maxLength={100}
                  placeholder="manual / ທະນາຄານ / …"
                  value={fxSource}
                  onChange={(e) => setFxSource(e.target.value)}
                  disabled={!shopId}
                />
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <p className="text-muted-foreground text-xs leading-relaxed">
                  ບັນທຶກທຸກແຖວທີ່ໃສ່ຕົວເລກແລ້ວເປັນຊຸດດຽວ (ສ້າງ effective ໃໝ່). API:{" "}
                  <span className="font-mono">POST …/exchange-rates/bulk</span>
                </p>
                <Button
                  type="button"
                  className="w-full shrink-0 sm:w-auto"
                  disabled={
                    !shopId ||
                    bulkExchangeRates.isPending ||
                    exchangeRatesQuery.isLoading
                  }
                  onClick={() => {
                    if (!shopId) return;
                    const rates: BulkCreateExchangeRatesDTO["rates"] = [];
                    for (const { from, to } of SHOP_FX_EDIT_PAIRS) {
                      const raw = (fxRates[exchangeRateKey(from, to)] ?? "")
                        .trim()
                        .replace(/,/g, "");
                      if (raw === "") continue;
                      const rate = Number.parseFloat(raw);
                      if (!Number.isFinite(rate) || rate <= 0) {
                        toast.error(`ອັດຕາ ${from} → ${to} ບໍ່ຖືກຕ້ອງ`);
                        return;
                      }
                      rates.push({
                        baseCurrency: from,
                        quoteCurrency: to,
                        rate,
                      });
                    }
                    if (rates.length === 0) {
                      toast.error("ກະລຸນາໃສ່ຢ່າງນ້ອຍໜຶ່ງອັດຕາ");
                      return;
                    }
                    void bulkExchangeRates.mutateAsync({
                      rates,
                      source: fxSource.trim() || undefined,
                    });
                  }}
                >
                  {bulkExchangeRates.isPending ? <Loader /> : null}
                  <span
                    className={bulkExchangeRates.isPending ? "ml-2" : ""}
                  >
                    ບັນທຶກອັດຕາທັງໝົດ
                  </span>
                </Button>
              </div>
            </div>
          </RequireMerchantPermission>
        </CardContent>
      </Card>
      <ShopExchangeRateHistorySection shopId={shopId} />
      </div>
    </RequireMerchantPermission>
  );
}
